import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { RefreshCw, Search, MessageSquare, Clock, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { logsAPI } from '../lib/api';

const LogsPanel = ({ agentId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await logsAPI.list(agentId);
      setLogs(data);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const filteredLogs = logs.filter(log => 
    log.user_message.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.assistant_response.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-white">{logs.length}</div>
            <p className="text-xs text-slate-400">Total Conversations</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-white">
              {logs.reduce((acc, log) => acc + log.tokens_used, 0)}
            </div>
            <p className="text-xs text-slate-400">Tokens Used</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-white">
              {logs.length > 0 ? Math.round(logs.reduce((acc, log) => acc + log.latency_ms, 0) / logs.length) : 0}ms
            </div>
            <p className="text-xs text-slate-400">Avg Response Time</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-white">
              {new Set(logs.map(l => l.origin_domain)).size}
            </div>
            <p className="text-xs text-slate-400">Unique Domains</p>
          </CardContent>
        </Card>
      </div>

      {/* Logs List */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-white">Conversation Logs</CardTitle>
              <CardDescription className="text-slate-400">Recent chat interactions</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search logs..."
                  className="pl-9 bg-slate-800 border-slate-700 text-white w-[200px]"
                />
              </div>
              <Button 
                variant="outline" 
                size="icon"
                onClick={loadLogs}
                disabled={loading}
                className="border-slate-700 text-slate-400 hover:text-white"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse bg-slate-800 rounded-lg h-24"></div>
                ))}
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="h-6 w-6 text-slate-500" />
                </div>
                <p className="text-slate-400 text-sm">
                  {searchQuery ? 'No logs match your search' : 'No conversation logs yet'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {filteredLogs.map((log) => (
                  <div 
                    key={log.log_id} 
                    className="p-4 hover:bg-slate-800/50 transition-colors cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.log_id ? null : log.log_id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatRelativeTime(log.created_at)}
                          </Badge>
                          {log.origin_domain && (
                            <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                              <Globe className="h-3 w-3 mr-1" />
                              {log.origin_domain}
                            </Badge>
                          )}
                          {log.sources_used.length > 0 && (
                            <Badge variant="outline" className="border-indigo-500/50 text-indigo-400 text-xs">
                              RAG: {log.sources_used.length} source{log.sources_used.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-white font-medium mb-1 line-clamp-1">
                          {log.user_message}
                        </p>
                        <p className="text-sm text-slate-400 line-clamp-2">
                          {log.assistant_response}
                        </p>
                        
                        {/* Expanded Content */}
                        {expandedLog === log.log_id && (
                          <div className="mt-4 p-3 bg-slate-800/50 rounded-lg space-y-3">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">User Message</p>
                              <p className="text-sm text-white">{log.user_message}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Assistant Response</p>
                              <p className="text-sm text-slate-300">{log.assistant_response}</p>
                            </div>
                            <div className="flex gap-4 text-xs text-slate-500">
                              <span>Tokens: {log.tokens_used}</span>
                              <span>Latency: {log.latency_ms}ms</span>
                              <span>Session: {log.session_id}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatTime(log.created_at)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xs text-slate-500">{log.tokens_used} tokens</span>
                        {expandedLog === log.log_id ? (
                          <ChevronUp className="h-4 w-4 text-slate-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-500" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogsPanel;
